import {useState} from 'react';

import {useQuery} from '@tanstack/react-query';
import type {NextPage} from 'next';
import {useTheme} from 'next-themes';
import {CircularProgressbar} from 'react-circular-progressbar';

import {parseFinancial} from 'utils/Financials';

import {getDashboardStats, getDashboardTxs} from 'api/v2/dashboard';

import useNetwork from 'hooks/use-network';
import useCurrentApp from 'hooks/useCurrentApp';

import AppLoader from 'components/Apps/AppLoader/AppLoader';
import {OverviewRevenueChart} from 'components/v2/app/dashboard/OverviewRevenueChart';
import {DashboardOverviewStats} from 'components/v2/app/dashboard/OverviewStats';
import {OverviewTotalRevenue} from 'components/v2/app/dashboard/OverviewTotalRevenue';
import {ExpenseCard} from 'components/v2/app/ExpenseCard';
import {RoundedCategoryBtn} from 'components/v3/button';
import {PageHeader} from 'components/v3/common/page-header/page-header';

const YearlyGoal = ({
  currentYearlyRevenue,
  yearlyGoal,
  percentage,
  progressbarStyles,
}: {
  currentYearlyRevenue: number;
  yearlyGoal: number;
  percentage: number;
  progressbarStyles: any;
}) => {
  return (
    <div
      className={'h-full flex flex-col gap-2 pb-[20px] max-md:justify-center'}>
      <div className={'p-[20px_20px]'}>
        <span className={'text-[16px] font-bold'}>Year Progress</span>
      </div>
      <div className={'w-1/2 flex gap-1 mx-auto text-center'}>
        <span className={'text-[14px] font-light text-center'}>
          ${currentYearlyRevenue}
        </span>
        <span className={'text-[14px] font-light text-[#8A8A8A]'}>
          / ${parseFinancial(yearlyGoal)}
        </span>
      </div>
      <div
        className={
          'flex flex-col w-[75%] gap-5 max-md:w-full max-md:p-[25px_25px]'
        }>
        <div className={'mx-auto'}>
          <div
            className={
              'w-[200px] h-[200px] max-md:!w-fit max-md:justify-center'
            }>
            <CircularProgressbar
              strokeWidth={6}
              value={percentage}
              text={`${parseFinancial(percentage)}%`}
              styles={progressbarStyles}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardPage: NextPage = () => {
  const {networkType} = useNetwork();

  const {id: appId} = useCurrentApp() || {};
  const {data} = useQuery({
    queryKey: ['app-stats', appId, networkType],
    queryFn: () => getDashboardStats(appId),
  });

  const {data: transactions} = useQuery({
    queryKey: ['app-txs', appId, networkType],
    queryFn: () => getDashboardTxs(appId),
  });

  const currentYear = new Date().getFullYear();

  const [overviewFilter, setOverviewFilter] = useState<string>('day');
  /*
  transactions = [
    {
      created_at: '2024-02-02',
      amount: 100,
    },
  ];*/

  const yearlyGoal = 50000; // 50k
  const currentYearlyRevenue = Number(
    parseFinancial((data?.year && data.year[currentYear]?.revenue) || 0),
  );
  const percentage = (currentYearlyRevenue * yearlyGoal) / 100;

  const {theme} = useTheme();

  const progressbarStyles = {
    path: {
      stroke: theme === 'dark' ? '#FFFFFF' : '#2B53E1',
    },
    trail: {
      stroke: theme === 'dark' ? '#313131' : '#FAFAFA',
    },
    text: {
      fill: theme === 'dark' ? '#FFF' : '#000',
      fontSize: '16px',
      fontWeight: 'bold',
    },
  };

  return (
    <>
      <div
        className={
          'grid grid-cols-[50%_22.5%_27.5%] p-[10px_10px] gap-10 divide-x noramp-bg max-md:flex max-md:flex-col'
        }>
        <div className={'w-full !p-[20px_20px]'}>
          <div className={'flex flex-col gap-10 h-full'}>
            <div className={'flex justify-between w-full max-md:flex-col'}>
              <PageHeader title="Dashboard" />
              <div className={'flex gap-3'}>
                <RoundedCategoryBtn
                  active={overviewFilter === 'day'}
                  text={'Day'}
                  onClick={() => setOverviewFilter('day')}
                />
                <RoundedCategoryBtn
                  active={overviewFilter === 'week'}
                  text={'Week'}
                  onClick={() => setOverviewFilter('week')}
                />
                <RoundedCategoryBtn
                  active={overviewFilter === 'month'}
                  text={'Month'}
                  onClick={() => setOverviewFilter('month')}
                />
                <RoundedCategoryBtn
                  active={overviewFilter === 'year'}
                  text={'Year'}
                  onClick={() => setOverviewFilter('year')}
                />
              </div>
            </div>
            <div className={'h-full'}>
              {data && (
                <DashboardOverviewStats stats={data} period={overviewFilter} />
              )}
            </div>
          </div>
        </div>

        <div className={'dark:border-[#313131] max-sm:hidden block'}>
          {data && <OverviewTotalRevenue stats={data} />}
        </div>
        <div className="dark:border-[#313131] max-sm:hidden block">
          <YearlyGoal
            yearlyGoal={yearlyGoal}
            currentYearlyRevenue={currentYearlyRevenue}
            percentage={percentage}
            progressbarStyles={progressbarStyles}
          />
        </div>

        <div className={'max-md:block hidden'}>
          <div className={'grid grid-cols-2 divide-x'}>
            <div className={'dark:border-[#313131]'}>
              {data && <OverviewTotalRevenue stats={data} />}
            </div>
            <div className="dark:border-[#313131] mx-auto flex justify-center">
              <YearlyGoal
                yearlyGoal={yearlyGoal}
                currentYearlyRevenue={currentYearlyRevenue}
                percentage={percentage}
                progressbarStyles={progressbarStyles}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className={
          'h-full border-t-solid border-t-[1px] border-t-[#DADADA] dark:border-t-[#313131] noramp-bg'
        }>
        <div
          className={
            'w-full grid grid-cols-[78.1%_22%] h-full divide-x max-md:flex max-md:flex-col'
          }>
          <div className={'h-full p-[40px_40px]'}>
            {data && (
              <OverviewRevenueChart
                chartOpts={{
                  fill: {
                    type: 'solid',
                    colors: ['rgba(187, 202, 239, 0.12)'],
                  },
                  yaxis: {},
                  xaxis: {},
                  stroke: {
                    curve: 'smooth',
                    colors: ['#BBCAE3'],
                    width: 1,
                  },
                  grid: {
                    show: true,
                    borderColor: theme === 'dark' ? '#313131' : '#DADADA',
                  },
                }}
                stats={data || ({} as any)}
              />
            )}
          </div>
          <div
            className={
              'flex flex-col p-[20px_40px] gap-6 dark:border-[#313131]'
            }>
            <span className={'text-[16px] font-bold'}>Recent Transactions</span>
            <div className={'flex flex-col gap-4 p-[5px_5px]'}>
              {transactions?.map(tx => {
                return (
                  <ExpenseCard
                    key={tx.created_at}
                    amount={parseFloat(String(tx.amount)).toFixed(2)}
                    date={new Date(tx.created_at)}
                  />
                );
              })}
              {!transactions?.length && (
                <p className="text-gray-400 opacity-75">No transactions yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const Page = () => {
  return (
    <AppLoader>
      {() => (
        <>
          <DashboardPage />
        </>
      )}
    </AppLoader>
  );
};

export default Page;
